/**
 * F43-FC (D6/D7) — RemoveTeamMemberDialog.
 *
 * Confirms removing ONE user from ONE team (admin-actor). Composes the F02
 * `Dialog` primitive (the same one the user-detail page uses for note/delete).
 * Reason is OPTIONAL (matches `RemoveTeamMemberInputSchema.reason.optional()`),
 * so no type-to-confirm phrase — removing a member is reversible by re-invite,
 * unlike a team disband.
 *
 * Props mirror the `SuspendTeamDialog` / `DisbandTeamDialog` contract shape
 * (`{ open, loading, serverError, onClose, onSubmit }`) plus the member context
 * (`memberName`, `teamName`). The `{member}`/`{team}` placeholders are
 * interpolated manually here because ICU `{…}` interpolation does NOT fire
 * reliably in the vitest jsdom env (same trap the two team dialogs document).
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Dialog, FormField, Textarea } from '@bravemove/ui-web';
import { TEAM_SUSPENDED_REASON_MAX, type RemoveTeamMemberInput } from '@bravemove/types';

export interface RemoveTeamMemberDialogProps {
  readonly open: boolean;
  readonly teamName: string;
  readonly memberName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: RemoveTeamMemberInput) => void;
}

export function RemoveTeamMemberDialog({
  open,
  teamName,
  memberName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: RemoveTeamMemberDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReason('');
    }
  }, [open]);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    onSubmit(trimmed.length > 0 ? { reason: trimmed } : {});
  };

  // Resolve the ICU title manually (jsdom ICU-interpolation caveat — mirrors
  // SuspendTeamDialog / DisbandTeamDialog).
  const rawTitle = t('admin:f43fc.team.remove_member.title', {
    member: memberName,
    team: teamName,
    defaultValue: 'Remove {member} from {team}?',
  }) as string;
  const titleText =
    rawTitle.includes('{member}') || rawTitle.includes('{team}')
      ? `Remove ${memberName} from ${teamName}?`
      : rawTitle;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      loading={loading}
      title={titleText}
      description={t('admin:f43fc.team.remove_member.body', {
        defaultValue:
          'Removes this user from the team roster and the team chat. They can be re-invited later. The captain cannot be removed — transfer captaincy or disband the team instead.',
      })}
      confirmLabel={t('admin:f43fc.team.remove_member.confirm', { defaultValue: 'Remove member' })}
    >
      <div className="space-y-3" data-testid="f43fc-remove-member-dialog">
        <FormField
          label={t('admin:f43fc.team.remove_member.reason_label', {
            defaultValue: 'Reason (optional, audit log)',
          })}
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={TEAM_SUSPENDED_REASON_MAX}
            placeholder={t('admin:f43fc.team.remove_member.reason_placeholder', {
              defaultValue: 'Why are you removing this member?',
            })}
            data-testid="f43fc-remove-member-reason"
          />
        </FormField>
        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </div>
    </Dialog>
  );
}
